import {
  ContextSelector,
  createContext,
  useContextSelector,
} from '@fluentui/react-context-selector';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { xorBy } from 'es-toolkit';
import { useRouter } from 'next/router';
import { Dispatch, useCallback, useEffect, useReducer, useRef } from 'react';
import InfiniteViewer from 'react-infinite-viewer';
import { createEditor, Transforms } from 'slate';
import { withReact } from 'slate-react';

import { LeftMenuTabTypes } from '../components/_organisms/DesignerTabs/DesignerTabUtils';
import { PromptVariables } from '../components/dynamicPages/CreatePage/hooks/useLearningObjectiveRefinementHook';
import { RichTextEditorType } from '../components/SlideComponents/SlateRichTextEditor';
import {
  parseStringToSlateObject,
  selectAll,
  withMathElements,
} from '../components/SlideComponents/slateUtil';
import { Slide, SlideElement } from '../components/SlideComponents/slide.types';
import {
  isSlideElementAttachmentPassage,
  isSlideElementText,
  MAX_ZOOM,
  MIN_ZOOM,
  RICHTEXTEDITOR_ID_PREFIX,
} from '../components/SlideComponents/slideUtil';
import {
  LessonAttachment,
  LLMMessageDTO,
} from '../services/backendService/backendService';
import {
  PromptInputData,
  PromptSingleTestCaseResult,
  PromptSingleTestCaseResultType,
} from '../services/backendService/types';
import { MOBILE_TO_DESKTOP_BREAKPOINT_PX } from '../utils/creator';
import { useBreakPoint } from './useBreakPoint';
import { useMetrics } from './useMetrics';
import useMixpanelFlag from './useMixpanelFlag';

export type TabState = {
  tab: LeftMenuTabTypes;
  subTab?: 'newBlankSlide' | 'appSmash' | 'editAttachment' | 'newAttachment';
  extraState?: string;
  previousTab?: LeftMenuTabTypes;
  initialData?: {
    showActivityOptions?: boolean;
    droppedImageData?: { src: string; mimeType: string };
  };
  dataState?: {
    attachmentTab?: {
      attachmentId?: string;
    };
  };
};

export type ElementTransformationProperties = {
  id: string;
  transformX: number;
  transformY: number;
  transformWidth: number;
  transformHeight: number;
  transformAngle: number;
  transformScale?: number;
};

export enum DesignerUIStateActions {
  setSelectedTab = 'SET_SELECTED_TAB',
  setSelectedTabGoBack = 'SET_SELECTED_TAB_GO_BACK',
  clearDroppedImageData = 'CLEAR_DROPPED_IMAGE_DATA',
  setIsDesktop = 'SET_IS_DESKTOP',
  setLeftTabCollapsed = 'SET_LEFT_TAB_COLLAPSED',
  setSlideListCollapsed = 'SET_SLIDE_LIST_COLLAPSED',
  setShowTabPaneFullWidth = 'SET_SHOW_TAB_PANE_FULL_WIDTH',
  toggleSettingsModal = 'TOGGLE_SETTINGS_MODAL',
  toggleMediaModal = 'TOGGLE_MEDIA_MODAL',
  closeDrawer = 'CLOSE_DRAWER',
  selectNewSlide = 'SELECT_NEW_SLIDE',
  selectEditMedia = 'SELECT_EDIT_MEDIA',
  selectSlide = 'SELECT_SLIDE',
  setForceLoadingState = 'SET_FORCE_LOADING_STATE',
  setIsUserIdle = 'SET_IS_USER_IDLE',
  startEditingTextElement = 'START_EDITING_TEXT_ELEMENT',
  stopEditingTextElement = 'STOP_EDITING_TEXT_ELEMENT',
  changeTextElementBeingEdited = 'CHANGE_EDITING_TEXT_ELEMENT',
  transformElements = 'TRANSFORM_ELEMENTS',
  stopTransformingElement = 'STOP_TRANSFORMING_ELEMENT',
  selectTargetElements = 'SELECT_TARGETS',
  deselectElements = 'DESELECT_ELEMENT',
  setZoom = 'SET_ZOOM',
  clearTextElementHeight = 'CLEAR_TEXT_ELEMENT_HEIGHT',
  allowInteractionWithIframe = 'ALLOW_INTERACTION_WITH_IFRAME',
  changeSelectedElements = 'CHANGE_SELECTED_ELEMENT',
  setSelectedPromptId = 'SET_SELECTED_PROMPT_ID',
  disableArrowKeyNavigation = 'DISABLE_ARROW_KEY_NAVIGATION',
  setTouchDeviceElementClicked = 'SET_TOUCH_DEVICE_ELEMENT_CLICKED',
  enableArrowKeyNavigation = 'ENABLE_ARROW_KEY_NAVIGATION',
  setAITextElementId = 'SET_AI_TEXT_ELEMENT_ID',
  clearAiElements = 'CLEAR_AI_ELEMENTS',
  setAIImageElementId = 'SET_AI_IMAGE_ELEMENT_ID',
  setAITextPromptSourceId = 'SET_AI_TEXT_PROMPT_SOURCE_ID',
  setAIImagePromptSourceId = 'SET_AI_IMAGE_PROMPT_SOURCE_ID',
  resetAIConnection = 'RESET_AI_TEXT_CONNECTION',
  showTemplateAccessErrorModal = 'SHOW_TEMPLATE_ACCESS_ERROR_MODAL',
  showCopyLessonModal = 'SHOW_COPY_LESSON_MODAL',
  showPresentOrPreviewModal = 'SHOW_PRESENT_OR_PREVIEW_MODAL',
  hidePresentOrPreviewModal = 'HIDE_PRESENT_OR_PREVIEW_MODAL',

  // DATA state
  setTestcaseResults = 'SET_TESTCASE_RESULTS',
  setSelectedTestcaseResult = 'SET_SELECTED_TESTCASE_RESULTS',
  updatePromptDependentInputs = 'UPDATE_PROMPT_DEPENDENT_INPUTS',
  updatePromptDependentInputsBulk = 'UPDATE_PROMPT_DEPENDENT_INPUTS_BULK',

  // Agent actions
  setAgentHistory = 'SET_AGENT_HISTORY',
  agentStreamStarted = 'SET_AGENT_STREAM_STARTED',
  agentReset = 'RESET_AGENT',
  agentStreamStopped = 'SET_AGENT_STREAM_STOPPED',
  agentStreamError = 'SET_AGENT_STREAM_ERROR',
  agentStreamOnTokenReceived = 'SET_AGENT_STREAM_ON_TOKEN_RECEIVED',
  agentStreamOnStateChange = 'SET_AGENT_STREAM_ON_STATE_CHANGE',
  agentStreamOnComplete = 'SET_AGENT_STREAM_ON_COMPLETE',
  agentOnCompleteMessageReceived = 'SET_AGENT_ON_COMPLETE_MESSAGE_RECEIVED',

  // Initial lesson building actions
  startInitialLessonBuilding = 'START_INITIAL_LESSON_BUILDING',
  completeInitialLessonBuilding = 'COMPLETE_INITIAL_LESSON_BUILDING',
}

export type DesignerUIStateAction =
// we really gotta update prettier!
// eslint-disable-next-line prettier/prettier
  | { type:  `${DesignerUIStateActions.setSelectedTab}`; payload: TabState }
  | { type:  `${DesignerUIStateActions.setSelectedTabGoBack}` }
  | { type:  `${DesignerUIStateActions.clearDroppedImageData}` }
  | { type:  `${DesignerUIStateActions.setIsDesktop}`; payload: boolean }
  | { type:  `${DesignerUIStateActions.toggleSettingsModal}` }
  | { type:  `${DesignerUIStateActions.toggleMediaModal}` }
  | { type:  `${DesignerUIStateActions.closeDrawer}` }
  | { type:  `${DesignerUIStateActions.selectNewSlide}` }
  | { type:  `${DesignerUIStateActions.selectSlide}`; payload: Slide }
  | {
      type:  `${DesignerUIStateActions.setForceLoadingState}`;
      payload: ForeceLoadingState;
    }
  | { type:  `${DesignerUIStateActions.setIsUserIdle}`; payload: boolean }
  | {
      type:  `${DesignerUIStateActions.setAgentHistory}`;
      payload: LLMMessageDTO[];
    }
  | {
      type:  `${DesignerUIStateActions.selectEditMedia}`;
      payload:
        | {
            src: string;
            mimeType: string;
          }
        | undefined;
    }
  | {
      type:  `${DesignerUIStateActions.startEditingTextElement}`;
      elementId: string;
    }
  | {
      type:  `${DesignerUIStateActions.stopEditingTextElement}`;
    }
  | {
      type:  `${DesignerUIStateActions.changeTextElementBeingEdited}`;
      elementId: string;
    }
  | {
      type:  `${DesignerUIStateActions.transformElements}`;
      elementTransformationProperties: ElementTransformationProperties[];
      shouldSetTransformingElement: boolean;
    }
  | { type:  `${DesignerUIStateActions.stopTransformingElement}` }
  | {
      type:  `${DesignerUIStateActions.setTouchDeviceElementClicked}`;
      isTouched: boolean;
    }
  | {
      type:  `${DesignerUIStateActions.selectTargetElements}`;
      selectedTargetElements: string[];
    }
  | {
      type:  `${DesignerUIStateActions.setLeftTabCollapsed}`;
      isCollapsed: boolean;
    }
  | {
      type:  `${DesignerUIStateActions.setSlideListCollapsed}`;
      isCollapsed: boolean;
    }
  | {
      type:  `${DesignerUIStateActions.setShowTabPaneFullWidth}`;
      isExpanded: boolean;
    }
  | { type:  `${DesignerUIStateActions.deselectElements}` }
  | {
      type:  `${DesignerUIStateActions.setZoom}`;
      zoom?: number;
      zoomAction?: 'fit' | 'increase' | 'decrease';
      containerFitWidth?: number;
      containerFitHeight?: number;
      infiniteViewer?: InfiniteViewer;
    }
  | { type:  `${DesignerUIStateActions.clearTextElementHeight}` }
  | { type:  `${DesignerUIStateActions.allowInteractionWithIframe}` }
  | {
      type:  `${DesignerUIStateActions.changeSelectedElements}`;
      selectedElements: SlideElement[] | null;
    }
  | {
      type:  `${DesignerUIStateActions.setSelectedPromptId}`;
      selectedPromptId: string | undefined;
    }
  | {
      type:  `${DesignerUIStateActions.disableArrowKeyNavigation}`;
    }
  | {
      type:  `${DesignerUIStateActions.enableArrowKeyNavigation}`;
    }
  | {
      type:  `${DesignerUIStateActions.setAITextElementId}`;
      elementId: string;
      isPollOption?: boolean;
    }
  | {
      type:  `${DesignerUIStateActions.setAIImageElementId}`;
      elementId: string;
    }
  | {
      type:  `${DesignerUIStateActions.clearAiElements}`;
    }
  | {
      type:  `${DesignerUIStateActions.setAITextPromptSourceId}`;
      promptSourceId: string;
    }
  | {
      type:  `${DesignerUIStateActions.setAIImagePromptSourceId}`;
      promptImageSourceId: string;
    }
  | {
      type:  `${DesignerUIStateActions.resetAIConnection}`;
    }
  | {
      type:  `${DesignerUIStateActions.setTestcaseResults}`;
      payload: {
        id: string;
        inputs: PromptInputData[];
        slides: Slide[];
        lessonAttachments?: LessonAttachment[];
        allTestcasePromptOutputs?: PromptSingleTestCaseResult;
        isSuggestion: boolean;
      }[];
    }
  | {
      type:  `${DesignerUIStateActions.setSelectedTestcaseResult}`;
      payload: {
        id: string;
        inputs: PromptInputData[];
        slides: Slide[];
        lessonAttachments?: LessonAttachment[];
        error?: string;
      };
    }
  | {
      type:  `${DesignerUIStateActions.updatePromptDependentInputs}`;
      payload: {
        id: string;
        value: string;
        type: PromptSingleTestCaseResultType;
      };
    }
  | {
      type:  `${DesignerUIStateActions.updatePromptDependentInputsBulk}`;
      payload: {
        id: string;
        value: string;
        type: PromptSingleTestCaseResultType;
      }[];
    }
  | {
      type:  `${DesignerUIStateActions.showTemplateAccessErrorModal}`;
      errorType: 'not_found' | 'access_denied' | 'generic_error';
    }
  | {
      type:  `${DesignerUIStateActions.agentStreamStarted}`;
    }
  | {
      type:  `${DesignerUIStateActions.agentReset}`;
    }
  | {
      type:  `${DesignerUIStateActions.agentStreamError}`;
      payload: ErrorDTO;
    }
  | {
      type:  `${DesignerUIStateActions.agentStreamOnTokenReceived}`;
      payload: {
        token: string;
      };
    }
  | {
      type:  `${DesignerUIStateActions.agentStreamOnStateChange}`;
      payload: {
        state: 'thinking' | 'generating' | null;
      };
    }
  | {
      type:  `${DesignerUIStateActions.agentStreamOnComplete}`;
      payload: {
        isComplete: boolean;
      };
    }
  | {
      type:  `${DesignerUIStateActions.agentOnCompleteMessageReceived}`;
      payload: {
        message: LLMMessageDTO;
      };
    }
  | {
      type:  `${DesignerUIStateActions.startInitialLessonBuilding}`;
    }
  | {
      type:  `${DesignerUIStateActions.agentStreamStopped}`;
    }
  | {
      type:  `${DesignerUIStateActions.completeInitialLessonBuilding}`;
    }
  | {
      type:  `${DesignerUIStateActions.showTemplateAccessErrorModal}`;
      errorType: 'not_found' | 'access_denied' | 'generic_error';
    }
  | {
      type:  `${DesignerUIStateActions.showCopyLessonModal}`;
    }
  | {
      type:  `${DesignerUIStateActions.showPresentOrPreviewModal}`;
    }
  | {
      type:  `${DesignerUIStateActions.hidePresentOrPreviewModal}`;
    };

type ForeceLoadingState = 'loading' | 'success' | undefined;
type DesignerUIState = {
  selectedTab: TabState;
  isDesktop: boolean;
  forceLoadingState: ForeceLoadingState;
  isUserIdle: boolean;
  isLeftTabCollapsed: boolean;
  isSlideListCollapsed: boolean;
  showSettingsModal: boolean;
  showMediaModal: boolean;
  isDrawerOpen: boolean;
  uploadedSlidesCache?: Slide[];
  textElementIdBeingEdited: string | undefined;
  elementTransformationProperties: ElementTransformationProperties[] | undefined;
  anElementIsBeingTransformed: boolean;
  selectedTargetElements: string[];
  canInteractWithIframe: boolean;
  showTabPaneFullWidth: boolean;
  selectedElements: SlideElement[] | null;
  selectedPromptId: string | undefined;
  richTextEditor: RichTextEditorType | null;
  selectedRichTextEditors: RichTextEditorType[];
  isArrowKeyNavigationDisabled: boolean;
  zoom: number;

  aiConnection?: {
    aiTextElementId?: string;
    aiImageElementId?: string;
    promptSourceId?: string;
    promptImageSourceId?: string;
    isPollOption?: boolean;
  };
  selectedTestcaseResult?: {
    id: string;
    inputs: PromptInputData[];
    slides: Slide[];
    allTestcasePromptOutputs?: PromptSingleTestCaseResult;
    lessonAttachments?: LessonAttachment[];
    error?: string;
  };

  // Loaded from cache. Used to update the prompt inputs across all the presentation prompts and ai feedback prompts
  promptDependentInputs?: PromptSingleTestCaseResult | undefined;
  testcaseResults?: {
    id: string;
    inputs: PromptInputData[];
    slides: Slide[];
    allTestcasePromptOutputs?: PromptSingleTestCaseResult;
    lessonAttachments?: LessonAttachment[];
    isSuggestion: boolean;
  }[];
  templateAccessErrorModal: {
    isVisible: boolean;
    errorType: 'not_found' | 'access_denied' | 'generic_error' | null;
  };
  copyLessonModal: {
    isVisible: boolean;
  };
  isPresentOrPreviewModalVisible: boolean;

  agent: {
    agentHistory: LLMMessageDTO[];
    isStreaming: boolean;
    error: ErrorDTO | null;
    tokens: string[];
    isComplete: boolean;
    currentContent: string;
    state: 'thinking' | 'generating' | null;
  };
  initialLessonBuilding?: {
    isLoading: boolean;
    params: PromptVariables;
    started: boolean;
  };
};

export type ErrorDTO<T = unknown> = {
  error: {
    code: number;
    message: string;
    type: string;
    errors?: T[];
  };
};

const createInitialState = ({
  initialPromptDependentInputs,
  initialLessonBuilding,
  activities,
}: {
  initialPromptDependentInputs?: PromptSingleTestCaseResult;
  initialLessonBuilding?: {
    params: PromptVariables;
  };
  activities?: boolean;
}): DesignerUIState => ({
  selectedTab: activities ? { tab: 'none' } : { tab: 'agent' },
  isDesktop: true,
  showSettingsModal: false,
  showMediaModal: false,
  isDrawerOpen: false, // Start with drawer closed, will be opened if on desktop
  isLeftTabCollapsed: activities ? true : false,
  isSlideListCollapsed: false,
  zoom: 1,
  textElementIdBeingEdited: undefined,
  elementTransformationProperties: undefined,
  anElementIsBeingTransformed: false,
  selectedTargetElements: [],
  canInteractWithIframe: false,
  showTabPaneFullWidth: false,
  selectedElements: null,
  selectedPromptId: undefined,
  richTextEditor: null,
  selectedRichTextEditors: [],
  isArrowKeyNavigationDisabled: false,
  testcaseResults: undefined,
  selectedTestcaseResult: undefined,
  forceLoadingState: undefined,
  isUserIdle: false,
  promptDependentInputs: initialPromptDependentInputs ?? {},
  agent: {
    agentHistory: [],
    isStreaming: false,
    error: null,
    tokens: [],
    isComplete: false,
    currentContent: '',
    state: null,
  },
  initialLessonBuilding: initialLessonBuilding
    ? {
        isLoading: true,
        started: false,
        params: initialLessonBuilding.params,
      }
    : undefined,
  templateAccessErrorModal: {
    isVisible: false,
    errorType: null,
  },
  copyLessonModal: {
    isVisible: false,
  },
  isPresentOrPreviewModalVisible: false,
});

type DesignerUIStateContextType = {
  state: DesignerUIState;
  dispatch: Dispatch<DesignerUIStateAction>;
  handleShowSettingsModal: () => void;
  handleShowSettingsModalCancel: () => void;
  handleMediaSelected: (
    imageData?:
      | {
          src: string;
          mimeType: string;
        }
      | undefined,
  ) => void;
  handleCloseDrawer: () => void;
  setSelectedElements: (newUpdate: SlideElement[] | null) => void;
  setSelectedPromptId: (promptId: string | undefined) => void;
  deselectElements: () => void;
  handleSetForceLoadingState: (forceLoadingState: ForeceLoadingState) => void;
  setIsUserIdle: (isUserIdle: boolean) => void;
  showTemplateAccessErrorModal: (
    errorType: 'not_found' | 'access_denied' | 'generic_error',
  ) => void;
  showCopyLessonModal: () => void;
  showPresentOrPreviewModal: () => void;
  hidePresentOrPreviewModal: () => void;
};

export const DesignerUIStateContext = createContext<DesignerUIStateContextType>({
  state: createInitialState({}),
  dispatch: () => {
    /**/
  },
  setIsUserIdle: () => {
    /**/
  },
  handleSetForceLoadingState: () => {
    /**/
  },
  handleMediaSelected: () => {
    /**/
  },
  handleShowSettingsModal: () => {
    /**/
  },
  handleShowSettingsModalCancel: () => {
    /**/
  },
  handleCloseDrawer: () => {
    /**/
  },
  setSelectedElements: () => {
    /**/
  },
  deselectElements: () => {
    /**/
  },
  setSelectedPromptId: () => {
    /**/
  },
  showTemplateAccessErrorModal: () => {
    /**/
  },
  showCopyLessonModal: () => {
    /**/
  },
  showPresentOrPreviewModal: () => {
    /**/
  },
  hidePresentOrPreviewModal: () => {
    /**/
  },
});

export function useDesignerUIContext<T>(
  selector: ContextSelector<DesignerUIStateContextType, T>,
) {
  return useContextSelector(DesignerUIStateContext, selector);
}

export const DesignerUIStateProvider = DesignerUIStateContext.Provider;

function reducer(state: DesignerUIState, action: DesignerUIStateAction): DesignerUIState {
  const { type } = action;
  switch (type) {
    case DesignerUIStateActions.setIsDesktop: {
      if (state.isDesktop === action.payload) {
        return state;
      } else {
        if (state.selectedTab.tab === 'none' && action.payload) {
          return {
            ...state,
            selectedTab: { tab: 'addNewSlideOrActivity' },
            isDesktop: action.payload,
            isDrawerOpen: action.payload, // Open drawer on desktop, close on mobile
          };
        }
        return {
          ...state,
          isDesktop: action.payload,
          isDrawerOpen: action.payload, // Open drawer on desktop, close on mobile
        };
      }
    }

    case DesignerUIStateActions.setForceLoadingState: {
      return {
        ...state,
        forceLoadingState: action.payload,
      };
    }
    case DesignerUIStateActions.setLeftTabCollapsed: {
      return {
        ...state,
        isLeftTabCollapsed: action.isCollapsed,
      };
    }
    case DesignerUIStateActions.setSlideListCollapsed: {
      return {
        ...state,
        isSlideListCollapsed: action.isCollapsed,
      };
    }
    case DesignerUIStateActions.setShowTabPaneFullWidth: {
      return {
        ...state,
        showTabPaneFullWidth: action.isExpanded,
      };
    }
    case DesignerUIStateActions.setSelectedPromptId: {
      return {
        ...state,
        selectedPromptId: action.selectedPromptId,
      };
    }

    case DesignerUIStateActions.setIsUserIdle: {
      return {
        ...state,
        isUserIdle: action.payload,
      };
    }

    case DesignerUIStateActions.setSelectedTab: {
      const previousTab = state.selectedTab.tab;
      if (action.payload.tab === 'none' && state.isDesktop) {
        return {
          ...state,
          selectedTab: { tab: 'addNewSlideOrActivity', previousTab },
        };
      }
      return {
        ...state,
        selectedTab: { ...action.payload, previousTab },
        isDrawerOpen: !state.isDesktop ? true : state.isDrawerOpen,
        isLeftTabCollapsed: false,
        showTabPaneFullWidth:
          action.payload.tab === 'testCases' ||
          action.payload.tab === 'testCasesResult' ||
          action.payload.tab === 'editAiFeedback'
            ? true
            : false,
      };
    }
    case DesignerUIStateActions.setSelectedTabGoBack: {
      const currentTab = state.selectedTab.tab;
      const previousTab = state.selectedTab.previousTab;
      if (!previousTab || previousTab === 'none') {
        return {
          ...state,
          selectedTab: { tab: 'addNewSlideOrActivity', previousTab: currentTab },
        };
      }
      return {
        ...state,
        selectedTab: { tab: previousTab, previousTab: currentTab },
      };
    }
    case DesignerUIStateActions.clearDroppedImageData:
      return {
        ...state,
        selectedTab: {
          ...state.selectedTab,
          initialData: {
            droppedImageData: undefined,
          },
        },
      };
    case DesignerUIStateActions.toggleSettingsModal:
      return {
        ...state,
        showSettingsModal: !state.showSettingsModal,
      };
    case DesignerUIStateActions.toggleMediaModal:
      return {
        ...state,
        showMediaModal: !state.showMediaModal,
      };
    case DesignerUIStateActions.closeDrawer:
      return {
        ...state,
        isDrawerOpen: false,
      };
    case DesignerUIStateActions.selectNewSlide:
      return {
        ...state,
        selectedTab: {
          tab: 'addNewSlideOrActivity',
        },
      };
    case DesignerUIStateActions.selectEditMedia:
      return {
        ...state,
        selectedTab: {
          tab: 'media',
          initialData: {
            droppedImageData: action.payload,
          },
        },
        isDrawerOpen: state.isDesktop ? false : true,
      };
    case DesignerUIStateActions.updatePromptDependentInputs: {
      const { id, value, type } = action.payload;
      const newTestCaseData: PromptSingleTestCaseResult = {
        ...state.promptDependentInputs,
        [id]: { value, id, type },
      };
      return {
        ...state,
        promptDependentInputs: newTestCaseData,
      };
    }
    case DesignerUIStateActions.updatePromptDependentInputsBulk: {
      const newTestCaseData = action.payload.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.id]: cur,
        }),
        state.promptDependentInputs,
      );

      return {
        ...state,
        promptDependentInputs: newTestCaseData,
      };
    }

    case DesignerUIStateActions.changeTextElementBeingEdited:
    case DesignerUIStateActions.startEditingTextElement: {
      return {
        ...state,
        textElementIdBeingEdited: action.elementId,
      };
    }
    case DesignerUIStateActions.stopEditingTextElement: {
      return {
        ...state,
        textElementIdBeingEdited: undefined,
      };
    }

    case DesignerUIStateActions.transformElements: {
      const newElementTransform = action.elementTransformationProperties.map(property => {
        const existingValue = state.elementTransformationProperties?.find(
          e => e.id === property.id,
        );
        return {
          id: property.id,
          transformX: property.transformX,
          transformY: property.transformY,
          transformWidth: property.transformWidth,
          transformHeight: property.transformHeight,
          transformAngle: property.transformAngle,
          transformScale: property.transformScale
            ? property.transformScale
            : existingValue?.transformScale,
        };
      });
      return {
        ...state,
        elementTransformationProperties: newElementTransform,
        anElementIsBeingTransformed: action.shouldSetTransformingElement,
      };
    }
    case DesignerUIStateActions.stopTransformingElement: {
      return {
        ...state,
        elementTransformationProperties: undefined,
        anElementIsBeingTransformed: false,
      };
    }
    case DesignerUIStateActions.selectTargetElements: {
      return {
        ...state,
        selectedTargetElements: action.selectedTargetElements,
      };
    }

    case DesignerUIStateActions.deselectElements: {
      return {
        ...state,
        selectedTargetElements: [],
        textElementIdBeingEdited: undefined,
        elementTransformationProperties: undefined,
        canInteractWithIframe: false,
        anElementIsBeingTransformed: false,
        selectedElements: null,
      };
    }
    case DesignerUIStateActions.setZoom: {
      if (
        action.zoomAction === 'fit' &&
        action.containerFitHeight &&
        action.containerFitWidth
      ) {
        const { containerFitWidth, infiniteViewer } = action;
        const containerFitHeight = action.containerFitHeight - 70; // 70px i the toolbar

        const aspectRatio = containerFitWidth / containerFitHeight;
        const slideAspectRatio = 1980 / 1080;
        let newZoom = 1;

        if (aspectRatio > slideAspectRatio) {
          // the width is the limiting factor
          newZoom = containerFitHeight / 1080;
          if (infiniteViewer) {
            // we remove the area of the toolbar from the height
            const toolbarHeight = 70 / newZoom;
            newZoom = containerFitHeight / (1080 + toolbarHeight);

            // calculate the remaining space
            const slideHeight = 1080;
            const deltaHeight = containerFitHeight / newZoom - slideHeight;
            const deltaWidth = containerFitWidth / newZoom - 1980;

            infiniteViewer.setZoom(newZoom);
            infiniteViewer.scrollTo(-deltaWidth / 2, -toolbarHeight - deltaHeight / 2);
          }
        } else {
          // the height is the limiting factor
          newZoom = containerFitWidth / 1980;
          if (infiniteViewer) {
            const toolbarHeight = 70 / newZoom;
            const deltaWidth = containerFitWidth / newZoom - 1980;
            const deltaHeight = containerFitHeight / newZoom - 1080;
            infiniteViewer.setZoom(newZoom);
            infiniteViewer.scrollTo(
              -deltaWidth / 2 - 25, // small adjustment
              -toolbarHeight - deltaHeight / 2,
            );
          }
        }

        return {
          ...state,
          zoom: newZoom,
        };
      }
      if (action.zoomAction === 'increase') {
        return {
          ...state,
          zoom: Math.min(state.zoom + 0.1, MAX_ZOOM),
        };
      }
      if (action.zoomAction === 'decrease') {
        return {
          ...state,
          zoom: Math.max(state.zoom - 0.1, MIN_ZOOM),
        };
      }
      if (action.zoom !== undefined) {
        return {
          ...state,
          zoom: action.zoom,
        };
      }

      return state;
    }
    case DesignerUIStateActions.allowInteractionWithIframe: {
      return {
        ...state,
        canInteractWithIframe: true,
      };
    }
    case DesignerUIStateActions.disableArrowKeyNavigation: {
      return {
        ...state,
        isArrowKeyNavigationDisabled: true,
      };
    }
    case DesignerUIStateActions.enableArrowKeyNavigation: {
      return {
        ...state,
        isArrowKeyNavigationDisabled: false,
      };
    }
    case DesignerUIStateActions.setTestcaseResults: {
      return {
        ...state,
        testcaseResults: action.payload,
      };
    }
    case DesignerUIStateActions.setSelectedTestcaseResult: {
      return {
        ...state,
        selectedTestcaseResult: action.payload,
      };
    }
    case DesignerUIStateActions.changeSelectedElements: {
      // Some logic when we already have an element selected
      const { selectedElements } = action;
      // const hasExistingElementSelected = state.selectedElement !== null;
      const baseResetElementSelectionState: Partial<DesignerUIState> = {
        textElementIdBeingEdited: undefined,
        elementTransformationProperties: undefined,
        selectedTargetElements: selectedElements
          ? selectedElements.map(e => `.target${e?.id}`)
          : [], // We store the classname. Its easy and not super complicated
      };
      if (
        state?.selectedElements &&
        selectedElements &&
        xorBy(state.selectedElements, selectedElements, ({ id }) => id).length === 0
      ) {
        // selection has not changed, but elements might have (specifically might have new x/y coords)
        // todo: refactor "changeSelectedElements" into two different actions for clarity: "change which elements are selected" and "change elements which are selected"
        return {
          ...state,
          selectedElements: action.selectedElements,
        };
      }

      // A single text element was selected
      if (selectedElements?.length === 1 && isSlideElementText(selectedElements[0])) {
        const selectedElement = selectedElements[0];
        if (
          !state.richTextEditor ||
          state.richTextEditor.id !== `${RICHTEXTEDITOR_ID_PREFIX}${selectedElement.id}`
        ) {
          const editor = withMathElements(withReact(createEditor()));
          editor.id = `${RICHTEXTEDITOR_ID_PREFIX}${selectedElement.id}`;
          const nodes = parseStringToSlateObject(selectedElement.textValue);

          Transforms.insertNodes(editor, nodes);
          selectAll(editor);

          return {
            ...state,
            ...baseResetElementSelectionState,
            selectedElements: action.selectedElements,
            selectedRichTextEditors: [editor],
            richTextEditor: editor,
          };
        } else {
          //dont reset base states if it is a text element
          return {
            ...state,
            elementTransformationProperties: undefined,
            selectedElements: action.selectedElements,
          };
        }
      } else if (
        selectedElements?.length === 1 &&
        isSlideElementAttachmentPassage(selectedElements[0])
      ) {
        /**
         * If we select an attachment, we open the attachment tab in edit mode
         */
        return {
          ...state,
          ...baseResetElementSelectionState,
          selectedElements: selectedElements,
          richTextEditor: null,
          isLeftTabCollapsed: false,
          selectedTab: {
            previousTab: state.selectedTab.tab,
            tab: 'attachments',
            subTab: 'editAttachment',
            dataState: {
              attachmentTab: {
                attachmentId: selectedElements[0].attachmentId,
              },
            },
          },
        };
      } else {
        const newSelectedElements =
          action.selectedElements && action.selectedElements.length > 0
            ? action.selectedElements
            : null;
        const selectedTextElements = newSelectedElements?.filter(
          e => e.type === 'text' || e.type === 'generatorText',
        );
        if (selectedTextElements && selectedTextElements?.length > 0) {
          const newEditors = selectedTextElements.map(e => {
            const editor = withMathElements(withReact(createEditor()));
            editor.id = `${RICHTEXTEDITOR_ID_PREFIX}${e.id}`;
            const nodes = parseStringToSlateObject(e.textValue);
            Transforms.insertNodes(editor, nodes);
            selectAll(editor);
            return editor;
          });
          return {
            ...state,
            ...baseResetElementSelectionState,
            selectedElements: newSelectedElements,
            richTextEditor: null,
            selectedRichTextEditors: newEditors,
          };
        }
        return {
          ...state,
          ...baseResetElementSelectionState,
          selectedElements: newSelectedElements,
          richTextEditor: null,
        };
      }
    }
    case DesignerUIStateActions.setAITextElementId: {
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          aiTextElementId: action.elementId,
          isPollOption: action.isPollOption,
        },
      };
    }
    case DesignerUIStateActions.setAIImageElementId: {
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          aiImageElementId: action.elementId,
        },
      };
    }
    case DesignerUIStateActions.clearAiElements: {
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          aiImageElementId: undefined,
          aiTextElementId: undefined,
        },
      };
    }
    case DesignerUIStateActions.setAITextPromptSourceId: {
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          promptSourceId: action.promptSourceId,
        },
      };
    }
    case DesignerUIStateActions.setAIImagePromptSourceId: {
      return {
        ...state,
        aiConnection: {
          ...state.aiConnection,
          promptImageSourceId: action.promptImageSourceId,
        },
      };
    }
    case DesignerUIStateActions.resetAIConnection: {
      return {
        ...state,
        aiConnection: undefined,
      };
    }

    case DesignerUIStateActions.showTemplateAccessErrorModal: {
      return {
        ...state,
        templateAccessErrorModal: {
          isVisible: true,
          errorType: action.errorType,
        },
      };
    }

    case DesignerUIStateActions.showCopyLessonModal: {
      return {
        ...state,
        copyLessonModal: {
          isVisible: true,
        },
      };
    }

    case DesignerUIStateActions.showPresentOrPreviewModal: {
      return {
        ...state,
        isPresentOrPreviewModalVisible: true,
      };
    }

    case DesignerUIStateActions.hidePresentOrPreviewModal: {
      return {
        ...state,
        isPresentOrPreviewModalVisible: false,
      };
    }

    case DesignerUIStateActions.setAgentHistory: {
      return {
        ...state,
        agent: {
          ...state.agent,
          agentHistory: action.payload,
        },
      };
    }
    case DesignerUIStateActions.agentStreamStarted: {
      return {
        ...state,
        agent: {
          ...state.agent,
          isStreaming: true,
          tokens: [],
          isComplete: false,
          currentContent: '',
          state: 'thinking',
          error: null,
        },
      };
    }
    case DesignerUIStateActions.agentReset: {
      return {
        ...state,
        agent: {
          ...state.agent,
          isStreaming: false,
          tokens: [],
          isComplete: false,
          currentContent: '',
          state: null,
          error: null,
        },
      };
    }
    case DesignerUIStateActions.agentStreamError: {
      return {
        ...state,

        agent: {
          ...state.agent,
          error: action.payload,
          isStreaming: false,
          state: null,
          currentContent: '',
          tokens: [],
        },
        initialLessonBuilding: undefined,
      };
    }
    case DesignerUIStateActions.agentStreamOnTokenReceived: {
      return {
        ...state,
        agent: {
          ...state.agent,
          tokens: [...state.agent.tokens, action.payload.token],
          currentContent: state.agent.currentContent + action.payload.token,
        },
      };
    }
    case DesignerUIStateActions.agentStreamOnStateChange: {
      return {
        ...state,
        agent: { ...state.agent, state: action.payload.state },
      };
    }
    case DesignerUIStateActions.agentStreamOnComplete: {
      return {
        ...state,
        agent: {
          ...state.agent,
          state: null,
          isComplete: action.payload.isComplete,
          isStreaming: false,
          tokens: [],
          currentContent: '',
        },
      };
    }
    case DesignerUIStateActions.agentStreamStopped: {
      return {
        ...state,
        agent: {
          ...state.agent,
          currentContent: '',
          error: null,
          isComplete: false,
          isStreaming: false,
          state: null,
          tokens: [],
        },
        initialLessonBuilding: undefined,
      };
    }
    case DesignerUIStateActions.agentOnCompleteMessageReceived: {
      return {
        ...state,
        agent: {
          ...state.agent,
          agentHistory: [...state.agent.agentHistory, action.payload.message],
          isComplete: false,
          isStreaming: true,
          tokens: [],
          currentContent: '',
        },
      };
    }
    case DesignerUIStateActions.startInitialLessonBuilding: {
      if (!state.initialLessonBuilding) {
        return state;
      }
      return {
        ...state,
        initialLessonBuilding: {
          ...state.initialLessonBuilding,
          isLoading: true,
          started: true,
        },
      };
    }
    case DesignerUIStateActions.completeInitialLessonBuilding: {
      if (!state.initialLessonBuilding) {
        return state;
      }
      return {
        ...state,
        initialLessonBuilding: undefined,
      };
    }
    case DesignerUIStateActions.showTemplateAccessErrorModal: {
      return {
        ...state,
        templateAccessErrorModal: {
          isVisible: true,
          errorType: action.errorType,
        },
      };
    }

    default:
      return state;
  }
}

export const setSelectedTab = (
  dispatch: Dispatch<DesignerUIStateAction>,
  tabState: TabState,
) => {
  dispatch({ type: DesignerUIStateActions.setSelectedTab, payload: tabState });
};

export function buildInitialLessonPromptVariablesQueryKey(presentationId: string) {
  return ['build-initial-lesson', 'prompt-variables', presentationId] as const;
}

const getInitialPromptVariables = (client: QueryClient, presentationId: string) => {
  const variables = client.getQueryData<PromptVariables>(
    buildInitialLessonPromptVariablesQueryKey(presentationId),
  );

  return variables;
};

export const useDesignerUIState = (presentationId: string) => {
  const metrics = useMetrics();
  const stateRef = useRef<DesignerUIState>();
  const isDesktop = useBreakPoint(MOBILE_TO_DESKTOP_BREAKPOINT_PX);
  const client = useQueryClient();
  const promptDependentInputsCache = client.getQueryData<PromptSingleTestCaseResult>([
    'persist',
    'prompt-inputs',
    presentationId,
  ]);
  const router = useRouter();

  const buildInitialLesson = router.query.buildInitialLesson;
  const activities = router.query.activities;


  const [state, dispatch] = useReducer(
    reducer,
    createInitialState({
      initialPromptDependentInputs: promptDependentInputsCache,
      initialLessonBuilding:
        buildInitialLesson === 'true' && getInitialPromptVariables(client, presentationId)
          ? {
              params: getInitialPromptVariables(
                client,
                presentationId,
              ) as PromptVariables,
            }
          : undefined,
          activities: activities === 'true' ? true : false,
    }),
  );

  useEffect(() => {
    if (buildInitialLesson && router.asPath.includes('buildInitialLesson')) {
      router.replace(router.asPath.replace('buildInitialLesson=true', ''));
    }
  }, [buildInitialLesson, router]);
 
  useEffect(() => {
     if (activities && router.asPath.includes('activities')) {
      router.replace(router.asPath.replace('activities=true', ''));
    }
  }, [activities, router]);
 
  useEffect(() => {
    // Persist isDesktop in the state
    dispatch({ type: DesignerUIStateActions.setIsDesktop, payload: isDesktop });
  }, [isDesktop]);

  const handleSetForceLoadingState = useCallback(
    (forceLoadingState: ForeceLoadingState) => {
      dispatch({
        type: DesignerUIStateActions.setForceLoadingState,
        payload: forceLoadingState,
      });
    },
    [],
  );

  const setIsUserIdle = useCallback((isUserIdle: boolean) => {
    dispatch({
      type: DesignerUIStateActions.setIsUserIdle,
      payload: isUserIdle,
    });
  }, []);

  const handleCloseDrawer = useCallback(() => {
    dispatch({ type: DesignerUIStateActions.closeDrawer });
  }, []);

  const handleMediaSelected = useCallback(
    (imageData?: { src: string; mimeType: string }) => {
      dispatch({ type: DesignerUIStateActions.selectEditMedia, payload: imageData });
    },
    [dispatch],
  );

  const handleShowSettingsModal = useCallback(() => {
    dispatch({ type: DesignerUIStateActions.toggleSettingsModal });
    metrics.logEvent('Creator.ShowSettingsModal');
  }, [metrics]);

  const handleShowSettingsModalCancel = useCallback(() => {
    dispatch({ type: DesignerUIStateActions.toggleSettingsModal });
    metrics.logEvent('Creator.ShowSettingsModal.Cancel');
  }, [metrics]);

  const setSelectedElements = useCallback(
    (newUpdate: SlideElement[] | null) => {
      dispatch({
        type: DesignerUIStateActions.changeSelectedElements,
        selectedElements: newUpdate,
      });
    },
    [dispatch],
  );
  const setSelectedPromptId = useCallback(
    (promptId: string | undefined) => {
      dispatch({
        type: DesignerUIStateActions.setSelectedPromptId,
        selectedPromptId: promptId,
      });
    },
    [dispatch],
  );
  const deselectElements = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.deselectElements,
    });
  }, [dispatch]);

  useEffect(() => {
    // Maintain a ref with the latest state so that we can do things upon unmounting of the hook.
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    // Upon unmounting, save the current state to local storage
    return () => {
      const cache = stateRef.current?.promptDependentInputs;
      if (stateRef.current && presentationId) {
        client.setQueryData(
          ['persist', 'prompt-inputs', presentationId],
          (existing: PromptSingleTestCaseResult | undefined) => {
            if (!existing) {
              return cache;
            }
            return {
              ...existing,
              ...cache,
            };
          },
        );
      }
    };
  }, [client, presentationId]);

  const showTemplateAccessErrorModal = useCallback(
    (errorType: 'not_found' | 'access_denied' | 'generic_error') => {
      dispatch({ type: DesignerUIStateActions.showTemplateAccessErrorModal, errorType });
    },
    [dispatch],
  );

  const showCopyLessonModal = useCallback(() => {
    dispatch({ type: DesignerUIStateActions.showCopyLessonModal });
  }, [dispatch]);

  const showPresentOrPreviewModal = useCallback(() => {
    dispatch({ type: DesignerUIStateActions.showPresentOrPreviewModal });
  }, [dispatch]);

  const hidePresentOrPreviewModal = useCallback(() => {
    dispatch({ type: DesignerUIStateActions.hidePresentOrPreviewModal });
  }, [dispatch]);

  return {
    dispatch,
    state,
    handleCloseDrawer,
    handleMediaSelected,
    handleShowSettingsModalCancel,
    handleShowSettingsModal,
    setSelectedElements,
    setSelectedPromptId,
    deselectElements,
    handleSetForceLoadingState,
    setIsUserIdle,
    showTemplateAccessErrorModal,
    showCopyLessonModal,
    showPresentOrPreviewModal,
    hidePresentOrPreviewModal,
  };
};
